import { storeRegistry } from "@/rerun-manager/GenericStore";
import { apiService } from "@/lib/api-functions/api-service";
import { UserProfile, Company, Game, UserContextData, CompanySummary } from "./storeTypes";
import { updateUserSettings } from "./profileStore";


/**
 * Unified store for all user-related data
 * This replaces userDataStore, userStore, and parts of authStore
 */
export const unifiedUserStore = storeRegistry.getStore<UserContextData>({
  key: 'unified_user_data',
  ttlMs: 1000 * 60 * 15, // 15 minutes cache
});

/**
 * Helper: Determine which company to select based on user settings and cached selections.
 * Extracted so it can be reused by both fetchUserContext (legacy) and the optimized flow.
 */
function determineSelectedCompany(
  companies: Array<{ id: string }>,
  userSettings: Record<string, any> | undefined,
  cachedCompanyId: string | undefined,
): string {
  const defaultProjectMode = userSettings?.defaultProjectMode || "last-used";

  // Priority 0: Preserve cached selection if still valid
  if (cachedCompanyId) {
    const companyStillExists = companies.find(c => c.id === cachedCompanyId);
    if (companyStillExists) {
      return cachedCompanyId;
    }
  }

  // Priority 1: "last-used" mode → use lastUsedCompany
  if (defaultProjectMode === "last-used" && userSettings?.lastUsedCompany) {
    const exists = companies.find(c => c.id === userSettings.lastUsedCompany);
    if (exists) return userSettings.lastUsedCompany;
  }

  // Priority 2: "specific" mode → use defaultCompany
  if (defaultProjectMode === "specific" && userSettings?.defaultCompany) {
    const exists = companies.find(c => c.id === userSettings.defaultCompany);
    if (exists) return userSettings.defaultCompany;
  }

  // Priority 3: Fall back to defaultCompany
  if (userSettings?.defaultCompany) {
    const exists = companies.find(c => c.id === userSettings.defaultCompany);
    if (exists) return userSettings.defaultCompany;
  }

  // Priority 4: First available
  if (companies.length > 0) return companies[0].id;

  throw new Error('No companies available for user');
}

/**
 * Helper: Determine which game to select within the given company.
 */
function determineSelectedGame(
  games: Game[],
  selectedCompanyId: string,
  userSettings: Record<string, any> | undefined,
  cachedGameId: string | undefined,
): string {
  const defaultProjectMode = userSettings?.defaultProjectMode || "last-used";
  const companyGames = games.filter(g => g.company_id === selectedCompanyId);

  // Priority 0: Preserve cached selection if still valid and belongs to company
  if (cachedGameId) {
    const game = games.find(g => g.id === cachedGameId);
    if (game && game.company_id === selectedCompanyId) {
      return cachedGameId;
    }
  }

  // Priority 1: "last-used" mode
  if (defaultProjectMode === "last-used" && userSettings?.lastUsedGame) {
    const game = games.find(g => g.id === userSettings.lastUsedGame);
    if (game && game.company_id === selectedCompanyId) {
      return userSettings.lastUsedGame;
    }
  }

  // Priority 2: "specific" mode
  if (defaultProjectMode === "specific" && userSettings?.defaultGame) {
    const game = games.find(g => g.id === userSettings.defaultGame);
    if (game && game.company_id === selectedCompanyId) {
      return userSettings.defaultGame;
    }
  }

  // Priority 3: defaultGame fallback
  if (userSettings?.defaultGame) {
    const game = games.find(g => g.id === userSettings.defaultGame);
    if (game && game.company_id === selectedCompanyId) {
      return userSettings.defaultGame;
    }
  }

  // Priority 4: First game from company
  if (companyGames.length > 0) return companyGames[0].id;

  // Priority 5: First game from any company (shouldn't normally happen)
  if (games.length > 0) return games[0].id;

  throw new Error('No games available for user');
}

/**
 * Convert a CompanySummary (from the lightweight endpoint) into a Company with empty member fields.
 * These placeholders are replaced with real data when the company is actually selected.
 */
function summaryToCompany(summary: CompanySummary): Company {
  return {
    id: summary.id,
    name: summary.name,
    created_at: summary.created_at,
    subscription: summary.subscription,
    licence_id: summary.licence_id,
    members: [],
    member_ids: [],
  };
}

/**
 * Fetch user context using the optimized two-step flow:
 *   1. GET /api/user-context-summary  → user profile + company list (lightweight)
 *   2. GET /api/user-context-company  → full data for the selected company
 *
 * This replaces the old single-call approach that loaded data for ALL companies at once.
 */
export async function fetchUserContext(): Promise<UserContextData> {
  try {
    const cachedData = unifiedUserStore.getData();
    const cachedCompanyId = cachedData.length > 0 ? cachedData[0].selectedCompanyId : undefined;
    const cachedGameId = cachedData.length > 0 ? cachedData[0].selectedGameId : undefined;

    // Step 1: Lightweight summary (user profile + company list)
    const summaryResult = await apiService.fetchUserContextSummary();
    if (!summaryResult.success || !summaryResult.data) {
      throw new Error(summaryResult.error || 'Failed to fetch user context summary');
    }
    const { currentUser, companies: companySummaries } = summaryResult.data;

    if (companySummaries.length === 0) {
      throw new Error('No companies available for user');
    }

    // Handle auto-set default company for single-company users
    let needsSettingsUpdate = false;
    const settingsUpdate: Record<string, unknown> = {};

    if (!currentUser.settings?.defaultCompany && companySummaries.length === 1) {
      settingsUpdate.defaultCompany = companySummaries[0].id;
      needsSettingsUpdate = true;
    }

    // Determine which company to select
    const selectedCompanyId = determineSelectedCompany(
      companySummaries,
      currentUser.settings,
      cachedCompanyId,
    );

    // Step 2: Fetch full data for the selected company
    const companyResult = await apiService.fetchCompanyContext(selectedCompanyId);
    if (!companyResult.success || !companyResult.data) {
      throw new Error(companyResult.error || 'Failed to fetch company context');
    }
    const companyContext = companyResult.data;

    // Build the companies array: full data for selected company, summary for the rest
    const companies: Company[] = companySummaries.map(summary =>
      summary.id === selectedCompanyId
        ? companyContext.company
        : summaryToCompany(summary)
    );

    // Determine which game to select
    let selectedGameId: string;
    try {
      selectedGameId = determineSelectedGame(
        companyContext.games,
        selectedCompanyId,
        currentUser.settings,
        cachedGameId,
      );
    } catch {
      // No games available — still allow loading (e.g., new company with no games yet)
      selectedGameId = '';
    }

    // Auto-set default game if needed
    if (!currentUser.settings?.defaultGame && companyContext.games.length > 0 && selectedGameId) {
      settingsUpdate.defaultGame = selectedGameId;
      needsSettingsUpdate = true;
    }

    // Save "last-used" settings if we didn't have cached selections
    const defaultProjectMode = currentUser.settings?.defaultProjectMode || "last-used";
    const hadCachedSelections = cachedData.length > 0 && cachedData[0].selectedCompanyId && cachedData[0].selectedGameId;
    if (defaultProjectMode === "last-used" && !hadCachedSelections) {
      if (!currentUser.settings?.lastUsedCompany || currentUser.settings.lastUsedCompany !== selectedCompanyId) {
        settingsUpdate.lastUsedCompany = selectedCompanyId;
        needsSettingsUpdate = true;
      }
      if (selectedGameId && (!currentUser.settings?.lastUsedGame || currentUser.settings.lastUsedGame !== selectedGameId)) {
        settingsUpdate.lastUsedGame = selectedGameId;
        needsSettingsUpdate = true;
      }
    }

    if (needsSettingsUpdate) {
      await updateUserSettings(settingsUpdate);
    }

    // Assemble final UserContextData (same shape as before for full backward compatibility)
    const dataWithSelections: UserContextData = {
      currentUser,
      companies,
      games: companyContext.games,
      allUserProfiles: companyContext.allUserProfiles,
      roles: companyContext.roles,
      userPermissions: companyContext.userPermissions,
      selectedCompanyId,
      selectedGameId,
    };

    await unifiedUserStore.syncFromRemote(async () => [dataWithSelections]);

    return dataWithSelections;
  } catch (error) {
    console.error('Error fetching user context:', error);
    throw error;
  }
}

/**
 * Switch to a different company: fetches full data for the new company
 * and updates the store. Called when the user picks a different company
 * in the CompanySwitcher dropdown.
 */
export async function switchToCompany(companyId: string): Promise<void> {
  const currentData = unifiedUserStore.getData();
  if (currentData.length === 0) {
    throw new Error('No user context loaded — cannot switch company');
  }
  const data = currentData[0];

  // Fetch full data for the target company
  const result = await apiService.fetchCompanyContext(companyId);
  if (!result.success || !result.data) {
    throw new Error(result.error || 'Failed to fetch company context');
  }
  const companyContext = result.data;

  // Update the companies array: replace the target company with full data
  const updatedCompanies = data.companies.map(c =>
    c.id === companyId ? companyContext.company : c
  );

  // Select first game from the new company, respecting user settings
  let selectedGameId: string;
  try {
    selectedGameId = determineSelectedGame(
      companyContext.games,
      companyId,
      data.currentUser.settings,
      undefined, // No cached game for the new company
    );
  } catch {
    selectedGameId = '';
  }

  const updatedData: UserContextData = {
    ...data,
    companies: updatedCompanies,
    games: companyContext.games,
    allUserProfiles: companyContext.allUserProfiles,
    roles: companyContext.roles,
    userPermissions: companyContext.userPermissions,
    selectedCompanyId: companyId,
    selectedGameId,
  };

  unifiedUserStore.setData([updatedData]);

  // Save "last-used" settings
  const defaultProjectMode = data.currentUser.settings?.defaultProjectMode || "last-used";
  if (defaultProjectMode === "last-used") {
    const settingsUpdate: Record<string, unknown> = {
      lastUsedCompany: companyId,
    };
    if (selectedGameId) {
      settingsUpdate.lastUsedGame = selectedGameId;
    }
    updateUserSettings(settingsUpdate).catch(err => {
      console.error('Failed to update last used company:', err);
    });
  }
}

/**
 * Re-fetch data for the currently selected company.
 * Used after game CRUD operations (create, update, delete) to refresh the game list
 * without doing a full two-step reload.
 */
export async function refreshCurrentCompanyData(): Promise<void> {
  const currentData = unifiedUserStore.getData();
  if (currentData.length === 0) return;

  const data = currentData[0];
  const companyId = data.selectedCompanyId;
  if (!companyId) return;

  const result = await apiService.fetchCompanyContext(companyId);
  if (!result.success || !result.data) {
    throw new Error(result.error || 'Failed to refresh company context');
  }
  const companyContext = result.data;

  const updatedCompanies = data.companies.map(c =>
    c.id === companyId ? companyContext.company : c
  );

  const updatedData: UserContextData = {
    ...data,
    companies: updatedCompanies,
    games: companyContext.games,
    allUserProfiles: companyContext.allUserProfiles,
    roles: companyContext.roles,
    userPermissions: companyContext.userPermissions,
  };

  // Preserve game selection if it still exists
  if (data.selectedGameId) {
    const gameStillExists = companyContext.games.find(g => g.id === data.selectedGameId);
    if (!gameStillExists && companyContext.games.length > 0) {
      updatedData.selectedGameId = companyContext.games[0].id;
    }
  }

  unifiedUserStore.setData([updatedData]);
}

/**
 * Check if we have valid cached data with selections
 * Returns true if cache has valid data, false otherwise
 */
export function hasCachedData(): boolean {
  const data = unifiedUserStore.getData();
  const hasData = data.length > 0 && 
    data[0].selectedGameId && 
    data[0].selectedCompanyId &&
    data[0].games.length > 0;
  
  return hasData;
}

/**
 * Get current user profile
 */
export function getCurrentUser(): UserProfile | null {
  const data = unifiedUserStore.getData();
  return data.length > 0 ? data[0].currentUser : null;
}

/**
 * Get all companies the user has access to
 */
export function getUserCompanies(): Company[] {
  const data = unifiedUserStore.getData();
  return data.length > 0 ? data[0].companies : [];
}

/**
 * Get all games the user has access to (games from the currently selected company)
 */
export function getUserGames(): Game[] {
  const data = unifiedUserStore.getData();
  return data.length > 0 ? data[0].games : [];
}

/**
 * Get games for a specific company
 */
export function getCompanyGames(companyId: string): Game[] {
  const games = getUserGames();
  return games.filter(game => game.company_id === companyId);
}

/**
 * Get all user profiles (for name resolution, avatars, etc.)
 */
export function getAllUserProfiles(): UserProfile[] {
  const data = unifiedUserStore.getData();
  return data.length > 0 ? data[0].allUserProfiles : [];
}

/**
 * Get user profile by ID
 */
export function getUserProfileById(userId: string): UserProfile | undefined {
  const profiles = getAllUserProfiles();
  return profiles.find(profile => profile.id === userId);
}

/**
 * Get user name by ID (fallback to email if name not available)
 */
export function getUserNameById(userId: string): string {
  const profile = getUserProfileById(userId);
  if (!profile) return 'Unknown User';

  return profile.full_name || profile.username || profile.email || 'Unknown User';
}

/**
 * Get company members (user IDs)
 */
export function getCompanyMemberIds(companyId: string): string[] {
  const companies = getUserCompanies();
  const company = companies.find(c => c.id === companyId);
  return company?.member_ids || [];
}

/**
 * Get company members with roles
 */
export function getCompanyMembersWithRoles(companyId: string): { id: string; role: string }[] {
  const companies = getUserCompanies();
  const company = companies.find(c => c.id === companyId);
  return company?.members || [];
}

/**
 * Get company owners
 */
export function getCompanyOwners(companyId: string): string[] {
  const membersWithRoles = getCompanyMembersWithRoles(companyId);
  return membersWithRoles.filter(member => member.role.toLowerCase() === 'owner').map(member => member.id);
}

/**
 * Check if user is a company owner
 */
export function isCompanyOwner(companyId: string, userId: string): boolean {
  const owners = getCompanyOwners(companyId);
  return owners.includes(userId);
}

/**
 * Get user role in company
 */
export function getUserRoleInCompany(companyId: string, userId: string): string | null {
  const membersWithRoles = getCompanyMembersWithRoles(companyId);
  const member = membersWithRoles.find(m => m.id === userId);
  return member?.role || null;
}

/**
 * Get company member profiles
 */
export function getCompanyMembers(companyId: string): UserProfile[] {
  const memberIds = getCompanyMemberIds(companyId);
  const allProfiles = getAllUserProfiles();
  return memberIds.map(id => allProfiles.find(p => p.id === id)).filter(Boolean) as UserProfile[];
}

/**
 * Get game members (user IDs)
 */
export function getGameMemberIds(gameId: string): string[] {
  const games = getUserGames();
  const game = games.find(g => g.id === gameId);
  return game?.member_ids || [];
}

/**
 * Get game member profiles
 */
export function getGameMembers(gameId: string): UserProfile[] {
  const memberIds = getGameMemberIds(gameId);
  const allProfiles = getAllUserProfiles();
  return memberIds.map(id => allProfiles.find(p => p.id === id)).filter(Boolean) as UserProfile[];
}

/**
 * Set selected company (synchronous local update only).
 * For switching companies with data fetch, use switchToCompany() instead.
 */
export function setSelectedCompany(companyId: string) {
  const currentData = unifiedUserStore.getData();
  if (currentData.length > 0) {
    const data = currentData[0];
    const updatedData = { ...data, selectedCompanyId: companyId };
    
    // Check if current game belongs to the new company
    const currentGame = data.games.find(g => g.id === data.selectedGameId);
    if (currentGame && currentGame.company_id !== companyId) {
      // Current game doesn't belong to new company, switch to first game from new company
      const companyGames = data.games.filter(g => g.company_id === companyId);
      if (companyGames.length > 0) {
        updatedData.selectedGameId = companyGames[0].id;
      }
    }
    
    unifiedUserStore.setData([updatedData]);
    
    // If user is in "last-used" mode, save this as the last used company
    const defaultProjectMode = data.currentUser.settings?.defaultProjectMode || "last-used";
    if (defaultProjectMode === "last-used") {
      // Save last used company (and game if we switched it)
      const settingsUpdate: Record<string, unknown> = {
        lastUsedCompany: companyId
      };
      
      if (updatedData.selectedGameId !== data.selectedGameId) {
        settingsUpdate.lastUsedGame = updatedData.selectedGameId;
      }
      
      // Update settings asynchronously (don't wait for it)
      updateUserSettings(settingsUpdate).catch(err => {
        console.error('Failed to update last used company:', err);
      });
    }
  }
}

/**
 * Set selected game
 */
export function setSelectedGame(gameId: string) {
  const currentData = unifiedUserStore.getData();
  if (currentData.length > 0) {
    const data = currentData[0];
    const updatedData = { ...data, selectedGameId: gameId };
    unifiedUserStore.setData([updatedData]);
    
    // If user is in "last-used" mode, save this as the last used game
    const defaultProjectMode = data.currentUser.settings?.defaultProjectMode || "last-used";
    if (defaultProjectMode === "last-used") {
      // Update settings asynchronously (don't wait for it)
      updateUserSettings({
        lastUsedGame: gameId
      }).catch(err => {
        console.error('Failed to update last used game:', err);
      });
    }
  }
}

/**
 * Get selected company
 */
export function getSelectedCompany(): Company | null {
  const data = unifiedUserStore.getData();
  if (data.length === 0) return null;

  const selectedId = data[0].selectedCompanyId;
  if (!selectedId) return null;

  return data[0].companies.find(c => c.id === selectedId) || null;
}

/**
 * Get selected game
 */
export function getSelectedGame(): Game | null {
  const data = unifiedUserStore.getData();
  if (data.length === 0) return null;

  const selectedId = data[0].selectedGameId;
  if (!selectedId) return null;

  return data[0].games.find(g => g.id === selectedId) || null;
}

/**
 * Subscribe to store changes
 */
export function subscribeToUserData(callback: () => void) {
  return unifiedUserStore.subscribe(callback);
}

/**
 * Update game information and refresh company data
 */
export async function updateGameInfo(
  gameId: string, 
  updates: {
    name?: string;
    description?: string;
    settings?: Record<string, unknown>;
  },
  toast?: (props: { title?: React.ReactNode; description?: React.ReactNode; variant?: string }) => void
): Promise<any> {
  try {
    // Update game via API
    const result = await apiService.updateGame(gameId, updates);
    
    if (!result.success || !result.data) {
      throw new Error(result.error || 'Failed to update game');
    }

    // Refresh only the current company's data
    await refreshCurrentCompanyData();
    
    if (toast) {
      toast({
        title: 'Success',
        description: 'Game updated successfully',
        variant: 'default',
      });
    }
    
    return result.data;
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Failed to update game';
    console.error('updateGameInfo - error:', error);
    
    if (toast) {
      toast({
        title: 'Error',
        description: message,
        variant: 'destructive',
      });
    }
    
    throw new Error(message);
  }
}

/**
 * Create a new game and refresh company data
 */
export async function createGame(
  gameData: {
    name: string;
    description?: string;
    company_id: string;
    settings?: Record<string, unknown>;
  },
  toast?: (props: { title?: React.ReactNode; description?: React.ReactNode; variant?: string }) => void
): Promise<any> {
  try {
    // Create game via API
    const result = await apiService.createGame(gameData);
    
    if (!result.success || !result.data) {
      throw new Error(result.error || 'Failed to create game');
    }

    // Refresh only the current company's data
    await refreshCurrentCompanyData();
    
    if (toast) {
      toast({
        title: 'Success',
        description: 'Game created successfully',
        variant: 'default',
      });
    }
    
    return result.data;
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Failed to create game';
    console.error('createGame - error:', error);
    
    if (toast) {
      toast({
        title: 'Error',
        description: message,
        variant: 'destructive',
      });
    }
    
    throw new Error(message);
  }
}

/**
 * Delete a game and refresh company data
 */
export async function deleteGame(
  gameId: string,
  toast?: (props: { title?: React.ReactNode; description?: React.ReactNode; variant?: string }) => void
): Promise<void> {
  try {
    // Delete game via API
    const result = await apiService.deleteGame(gameId);
    
    if (!result.success) {
      throw new Error(result.error || 'Failed to delete game');
    }

    // Refresh only the current company's data
    await refreshCurrentCompanyData();
    
    if (toast) {
      toast({
        title: 'Success',
        description: 'Game deleted successfully',
        variant: 'default',
      });
    }
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Failed to delete game';
    console.error('deleteGame - error:', error);
    
    if (toast) {
      toast({
        title: 'Error',
        description: message,
        variant: 'destructive',
      });
    }
    
    throw new Error(message);
  }
}

/**
 * Clear user data (for logout)
 */
export async function clearUserData() {
  await unifiedUserStore.clearCache();
}
